import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  FileText, CheckCircle, Clock, XCircle, AlertCircle,
  Loader2, ChevronRight, Pen, Type, ArrowLeft, RefreshCw
} from 'lucide-react';
import { signingApi, getToken, isTokenExpired, API_BASE_URL } from '../utils/api';

const statusConfig = {
  pending: { label: 'Pending', color: '#f59e0b', bg: '#fef3c7', icon: Clock },
  signed: { label: 'Signed', color: '#22c55e', bg: '#dcfce7', icon: CheckCircle },
  declined: { label: 'Declined', color: '#ef4444', bg: '#fef2f2', icon: XCircle },
  expired: { label: 'Expired', color: '#6b7280', bg: '#f3f4f6', icon: AlertCircle },
};

// ============================================================================
// Signature Pad Component
// ============================================================================
const SignaturePad = ({ onSignatureChange }) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);
  const [mode, setMode] = useState('draw'); // 'draw' or 'type'
  const [typedName, setTypedName] = useState('');

  const getCoords = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    if (e.touches) {
      return {
        x: (e.touches[0].clientX - rect.left) * scaleX,
        y: (e.touches[0].clientY - rect.top) * scaleY,
      };
    }
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY,
    };
  };

  const startDrawing = (e) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const { x, y } = getCoords(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const { x, y } = getCoords(e);
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#000';
    ctx.lineTo(x, y);
    ctx.stroke();
    setHasSignature(true);
  };

  const stopDrawing = () => {
    if (isDrawing) {
      setIsDrawing(false);
      if (hasSignature) {
        const canvas = canvasRef.current;
        onSignatureChange(canvas.toDataURL('image/png'), 'drawn');
      }
    }
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
    onSignatureChange(null, null);
  };

  const handleTypedChange = (e) => {
    setTypedName(e.target.value);
    if (e.target.value.trim()) {
      onSignatureChange(e.target.value.trim(), 'typed');
    } else {
      onSignatureChange(null, null);
    }
  };

  const switchMode = (newMode) => {
    setMode(newMode);
    clearSignature();
    setTypedName('');
    onSignatureChange(null, null);
  };

  return (
    <div className="signature-pad-container">
      <div className="signature-mode-tabs">
        <button
          className={`signature-mode-tab ${mode === 'draw' ? 'active' : ''}`}
          onClick={() => switchMode('draw')}
        >
          <Pen size={14} /> Draw
        </button>
        <button
          className={`signature-mode-tab ${mode === 'type' ? 'active' : ''}`}
          onClick={() => switchMode('type')}
        >
          <Type size={14} /> Type
        </button>
      </div>

      {mode === 'draw' ? (
        <div className="signature-canvas-wrapper">
          <canvas
            ref={canvasRef}
            width={600}
            height={200}
            className="signature-canvas"
            onMouseDown={startDrawing}
            onMouseMove={draw}
            onMouseUp={stopDrawing}
            onMouseLeave={stopDrawing}
            onTouchStart={startDrawing}
            onTouchMove={draw}
            onTouchEnd={stopDrawing}
          />
          {!hasSignature && (
            <div className="signature-placeholder">Sign here</div>
          )}
          {hasSignature && (
            <button className="clear-signature-btn" onClick={clearSignature}>
              Clear
            </button>
          )}
        </div>
      ) : (
        <div className="signature-typed-wrapper">
          <input
            type="text"
            className="signature-typed-input"
            placeholder="Type your full name"
            value={typedName}
            onChange={handleTypedChange}
          />
          {typedName && (
            <div className="signature-typed-preview">
              {typedName}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// ACH Form Component
// ============================================================================
const AchForm = ({ formFields, formData, onChange }) => {
  const handleChange = (fieldName, value) => {
    onChange({ ...formData, [fieldName]: value });
  };

  return (
    <div className="ach-form">
      <h3 className="ach-form-title">ACH Payment Information</h3>
      <p className="ach-form-description">
        Please provide your bank account information for ACH payment processing.
      </p>
      <div className="ach-form-fields">
        {formFields.map((field) => (
          <div key={field.name} className="form-group">
            <label>
              {field.label}
              {field.required && <span className="required-star"> *</span>}
            </label>
            {field.type === 'select' ? (
              <select
                value={formData[field.name] || ''}
                onChange={(e) => handleChange(field.name, e.target.value)}
                required={field.required}
              >
                <option value="">Select...</option>
                {field.options?.map((opt) => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            ) : (
              <input
                type={field.type || 'text'}
                value={formData[field.name] || ''}
                onChange={(e) => handleChange(field.name, e.target.value)}
                placeholder={field.placeholder || ''}
                required={field.required}
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

// ============================================================================
// Main Signing Package Page
// ============================================================================
const SigningPackage = () => {
  const { packageToken } = useParams();
  const navigate = useNavigate();

  const [packageData, setPackageData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  // Current document being viewed/signed
  const [activeSessionIndex, setActiveSessionIndex] = useState(null);
  const [activeDoc, setActiveDoc] = useState(null);
  const mainScrollRef = useRef(null);
  const [docLoading, setDocLoading] = useState(false);

  // Signing state
  const [signatureData, setSignatureData] = useState(null);
  const [signatureType, setSignatureType] = useState(null);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [formData, setFormData] = useState({});
  const [isSigning, setIsSigning] = useState(false);
  const [signError, setSignError] = useState('');
  const [signSuccess, setSignSuccess] = useState('');

  // Auth state
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [needsAuth, setNeedsAuth] = useState(false);

  useEffect(() => {
    const token = getToken();
    const authed = !!(token && !isTokenExpired(token));
    setIsAuthenticated(authed);
  }, [packageToken]);

  // Load package data
  const loadPackage = useCallback(async () => {
    try {
      setIsLoading(true);
      const data = await signingApi.getPackage(packageToken);
      setPackageData(data);

      // Auto-select first unsigned document
      if (!activeSessionIndex) {
        const firstUnsigned = data.sessions?.findIndex(s => s.signing_status === 'pending');
        if (firstUnsigned >= 0) {
          setActiveSessionIndex(firstUnsigned);
        } else {
          setActiveSessionIndex(0);
        }
      }
    } catch (err) {
      setError('Unable to load signing package. The link may be expired or invalid.');
    } finally {
      setIsLoading(false);
    }
  }, [packageToken]);

  useEffect(() => {
    loadPackage();
  }, [loadPackage]);

  // Load individual document when active session changes
  useEffect(() => {
    if (activeSessionIndex === null || !packageData?.sessions) return;
    const session = packageData.sessions[activeSessionIndex];
    if (!session) return;

    const loadDoc = async () => {
      setDocLoading(true);
      setSignError('');
      setSignSuccess('');
      setAcceptTerms(false);
      setSignatureData(null);
      setSignatureType(null);
      setFormData({});
      try {
        const doc = await signingApi.getDocument(session.signing_token);
        setActiveDoc(doc);
      } catch (err) {
        setActiveDoc(null);
      } finally {
        setDocLoading(false);
      }
    };
    loadDoc();
  }, [activeSessionIndex, packageData]);

  // When the active document changes — e.g. after signing the MSA the flow
  // auto-advances to the BAA — reset scroll to the top. Otherwise the new
  // document opens scrolled to the bottom of the previous one, making it look
  // like the sign button did nothing.
  useEffect(() => {
    if (activeSessionIndex === null) return;
    if (mainScrollRef.current) mainScrollRef.current.scrollTop = 0;
    window.scrollTo(0, 0);
  }, [activeSessionIndex]);

  const handleSignatureChange = (data, type) => {
    setSignatureData(data);
    setSignatureType(type);
  };

  // Employee onboarding packages have no portal account — the emailed
  // package token authorizes signing, so no login gate applies.
  const isEmployeePackage = packageData?.package_kind === 'employee_onboarding';

  const handleSign = async () => {
    if (!isAuthenticated && !isEmployeePackage) {
      setNeedsAuth(true);
      return;
    }

    const session = packageData.sessions[activeSessionIndex];
    if (!session) return;

    // Validate
    if (!signatureData) {
      setSignError('Please provide your signature.');
      return;
    }
    if (!acceptTerms) {
      setSignError('Please accept the terms to proceed.');
      return;
    }
    if (activeDoc?.requires_form_input) {
      const requiredFields = (activeDoc.form_fields || []).filter(f => f.required);
      for (const field of requiredFields) {
        if (!formData[field.name]?.trim()) {
          setSignError(`Please fill in the required field: ${field.label}`);
          return;
        }
      }
    }

    try {
      setIsSigning(true);
      setSignError('');
      await signingApi.signDocument(
        session.signing_token,
        signatureData,
        signatureType,
        acceptTerms,
        activeDoc?.requires_form_input ? formData : null,
        isEmployeePackage ? packageToken : null
      );

      setSignSuccess('Document signed successfully!');

      // Reload package to get updated statuses
      const updatedPackage = await signingApi.getPackage(packageToken);
      setPackageData(updatedPackage);

      // Auto-advance to next unsigned document after a brief pause
      setTimeout(() => {
        const nextUnsigned = updatedPackage.sessions?.findIndex(s => s.signing_status === 'pending');
        if (nextUnsigned >= 0) {
          setActiveSessionIndex(nextUnsigned);
          setSignSuccess('');
        }
      }, 1500);
    } catch (err) {
      setSignError(err.message || 'Failed to sign document. Please try again.');
    } finally {
      setIsSigning(false);
    }
  };

  // ============================================================================
  // Render
  // ============================================================================

  if (isLoading) {
    return (
      <div className="signing-loading">
        <Loader2 size={32} className="spinner-large" />
        <p>Loading documents...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="signing-error-page">
        <div className="signing-error-card">
          <AlertCircle size={48} color="#ef4444" />
          <h2>Unable to Load Documents</h2>
          <p>{error}</p>
          <p className="help-text">
            If you believe this is an error, please contact Macula Networks support.
          </p>
        </div>
      </div>
    );
  }

  if (!packageData) return null;

  const allSigned = packageData.sessions?.every(s => s.signing_status === 'signed');
  const activeSession = packageData.sessions?.[activeSessionIndex];

  // Auth redirect notice
  if (needsAuth) {
    return (
      <div className="signing-auth-required">
        <div className="signing-auth-card">
          <AlertCircle size={48} color="#f59e0b" />
          <h2>Authentication Required</h2>
          <p>You need to log in to your client portal account to sign documents.</p>
          <button
            className="submit-button"
            onClick={() => { window.location.href = `/login?redirect=/sign/${packageToken}`; }}
          >
            Log In to Continue
          </button>
        </div>
      </div>
    );
  }

  // All signed celebration
  if (allSigned) {
    // If this package was created from a proposal accept, advance the
    // client to /contract/:pathId where the onboarding gate (payment
    // method, etc.) shows next steps. Otherwise back to dashboard.
    const continueTarget = packageData.client_path_id
      ? `/contract/${packageData.client_path_id}`
      : '/';
    return (
      <div className="signing-complete-page">
        <div className="signing-complete-card">
          <CheckCircle size={64} color="#22c55e" />
          <h2>All Documents Signed!</h2>
          <p>
            Thank you for signing all required documents. You will receive confirmation
            via email.
          </p>
          {packageData.recipient_name && (
            <p className="signing-complete-name">Signed by {packageData.recipient_name}</p>
          )}
          <div style={{ marginTop: 24, display: 'flex', gap: 12, justifyContent: 'center' }}>
            <button
              type="button"
              className="btn-primary"
              onClick={() => { window.location.href = continueTarget; }}
              style={{
                padding: '10px 20px',
                background: '#3182ce',
                color: 'white',
                border: 'none',
                borderRadius: 6,
                fontSize: 14,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              {packageData.client_path_id ? 'Continue Onboarding' : 'Back to Portal'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="signing-page">
      {/* Header */}
      <div className="signing-header">
        <div className="signing-header-content">
          <h1>Document Signing</h1>
          <p className="signing-header-sub">
            Please review and sign the following documents
          </p>
        </div>
      </div>

      <div className="signing-layout">
        {/* Sidebar - Document List */}
        <div className="signing-sidebar">
          <div className="signing-sidebar-header">
            <h3>Documents ({packageData.sessions?.filter(s => s.signing_status === 'signed').length}/{packageData.sessions?.length})</h3>
          </div>
          <div className="signing-doc-list">
            {packageData.sessions?.map((session, idx) => {
              const config = statusConfig[session.signing_status] || statusConfig.pending;
              const StatusIcon = config.icon;
              const isActive = idx === activeSessionIndex;

              return (
                <button
                  key={session.id}
                  className={`signing-doc-item ${isActive ? 'active' : ''} ${session.signing_status}`}
                  onClick={() => setActiveSessionIndex(idx)}
                >
                  <div className="signing-doc-item-icon">
                    <StatusIcon size={18} color={config.color} />
                  </div>
                  <div className="signing-doc-item-info">
                    <span className="signing-doc-item-name">{session.document_name}</span>
                    <span
                      className="signing-doc-item-status"
                      style={{ color: config.color }}
                    >
                      {config.label}
                    </span>
                  </div>
                  {isActive && <ChevronRight size={16} className="signing-doc-item-arrow" />}
                </button>
              );
            })}
          </div>
        </div>

        {/* Main Content */}
        <div className="signing-main" ref={mainScrollRef}>
          {docLoading ? (
            <div className="signing-doc-loading">
              <Loader2 size={24} className="spinner" />
              <p>Loading document...</p>
            </div>
          ) : activeDoc ? (
            <>
              {/* Document Header */}
              <div className="signing-doc-header">
                <FileText size={20} />
                <h2>{activeDoc.document_name}</h2>
                {activeDoc.signing_status !== 'pending' && (
                  <span
                    className="signing-status-badge"
                    style={{
                      color: statusConfig[activeDoc.signing_status]?.color,
                      backgroundColor: statusConfig[activeDoc.signing_status]?.bg,
                    }}
                  >
                    {statusConfig[activeDoc.signing_status]?.label}
                  </span>
                )}
              </div>

              {/* Instructions */}
              {activeDoc.signing_instructions && (
                <div className="signing-instructions">
                  <p>{activeDoc.signing_instructions}</p>
                </div>
              )}

              {/* Document Content — rendered in an isolated iframe so the
                  document's own <style> (which targets bare body/h1/p) can't
                  leak into and re-style the portal. Auto-sized to full content
                  height so the page scrolls naturally instead of trapping the
                  whole MSA in a tiny inner scroll-box. */}
              <div className="signing-doc-content">
                {activeDoc.has_source_pdf ? (
                  /* Uploaded-PDF document (employee onboarding) — the browser's
                     native PDF viewer; token-authed public endpoint. */
                  <iframe
                    title={activeDoc.document_name || 'Document'}
                    className="signing-doc-frame"
                    src={`${API_BASE_URL}/api/portal/signing/${packageData.sessions[activeSessionIndex]?.signing_token}/pdf`}
                    style={{ height: '75vh', width: '100%', border: 'none' }}
                  />
                ) : activeDoc.rendered_html ? (
                  <iframe
                    title={activeDoc.document_name || 'Document'}
                    className="signing-doc-frame"
                    srcDoc={activeDoc.rendered_html}
                    onLoad={(e) => {
                      try {
                        const doc = e.target.contentDocument;
                        if (doc) {
                          e.target.style.height =
                            doc.documentElement.scrollHeight + 'px';
                        }
                      } catch (_) {
                        /* same-origin srcdoc; ignore if unreadable */
                      }
                      // Once the new document is rendered + sized, land at its
                      // top (the container height just changed under us).
                      if (mainScrollRef.current) mainScrollRef.current.scrollTop = 0;
                      window.scrollTo(0, 0);
                    }}
                  />
                ) : (
                  <div className="signing-doc-placeholder">
                    <FileText size={48} color="#d1d5db" />
                    <p>Document content is not available for preview.</p>
                  </div>
                )}
              </div>

              {/* Signing Area - Only show for pending docs */}
              {activeDoc.can_sign && activeDoc.signing_status === 'pending' && (
                <div className="signing-action-area">
                  {/* ACH Form (if required) */}
                  {activeDoc.requires_form_input && activeDoc.form_fields && (
                    <AchForm
                      formFields={activeDoc.form_fields}
                      formData={formData}
                      onChange={setFormData}
                    />
                  )}

                  {/* Signature Pad */}
                  <div className="signing-signature-section">
                    <h3>Your Signature</h3>
                    <SignaturePad onSignatureChange={handleSignatureChange} />
                  </div>

                  {/* Accept Terms */}
                  <label className="signing-accept-terms">
                    <input
                      type="checkbox"
                      checked={acceptTerms}
                      onChange={(e) => setAcceptTerms(e.target.checked)}
                    />
                    <span>
                      I have read, understand, and agree to the terms in this document. I acknowledge
                      that my electronic signature is legally binding.
                    </span>
                  </label>

                  {/* Error / Success */}
                  {signError && (
                    <div className="signing-error-msg">
                      <AlertCircle size={16} />
                      {signError}
                    </div>
                  )}
                  {signSuccess && (
                    <div className="signing-success-msg">
                      <CheckCircle size={16} />
                      {signSuccess}
                    </div>
                  )}

                  {/* Sign Button */}
                  <button
                    className="signing-sign-button"
                    onClick={handleSign}
                    disabled={isSigning || !signatureData || !acceptTerms}
                  >
                    {isSigning ? (
                      <>
                        <Loader2 size={18} className="spinner" />
                        Signing...
                      </>
                    ) : (
                      <>
                        <Pen size={18} />
                        Sign Document
                      </>
                    )}
                  </button>
                </div>
              )}

              {/* Already signed message */}
              {activeDoc.signing_status === 'signed' && (
                <div className="signing-already-signed">
                  <CheckCircle size={24} color="#22c55e" />
                  <div>
                    <strong>Document Signed</strong>
                    {activeDoc.signed_at && (
                      <p>Signed on {new Date(activeDoc.signed_at).toLocaleString()}</p>
                    )}
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="signing-doc-placeholder">
              <FileText size={48} color="#d1d5db" />
              <p>Select a document to view</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SigningPackage;
